import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '../_services/auth.service';
import { inject } from '@angular/core';
import { environment } from '../../environments/environment';
import { from, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

export const authInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const authService = inject(AuthService);
  
  // Skip interceptor for translation files to prevent auth errors blocking i18n
  if (request.url.includes('/assets/i18n/')) {
    return next(request);
  }

  // Skip interceptor for password recovery requests — they carry their own recovery token
  if (request.url.includes('/v1/auth/forgot')) {
    return next(request);
  }
  
  const isProtected = request.url.includes(environment.apiUrl) || request.url.includes(environment.nlpApi);

  const withAuthHeader = (req: HttpRequest<unknown>): HttpRequest<unknown> => {
    const token = authService.getAuth()?.accessToken;
    if (!token) return req;
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  };
  
  const hasOwnAuth = request.headers.has('Authorization');

  const handleRefreshFailure = (): void => {
    if (!authService.isLoggingOut && !authService.isRecordingActive) {
      console.log('[AuthInterceptor] Server rejected token, logging out');
      authService.logout();
    }
  };

  const initial$ = isProtected && !hasOwnAuth
    ? from(authService.ensureValidToken(60000)).pipe(
        catchError(() => {
          if (!authService.getAuth()?.accessToken) {
            return throwError(() => new Error('Session expired'));
          }
          return of(null);
        }),
        switchMap(() => next(withAuthHeader(request)))
      )
    : next(request);

  const isBusinessLogic401 = (error: HttpErrorResponse): boolean => {
    const message = error?.error?.message;
    if (message === 'verify-sms' || message === 'verify-email') return true;

    // Verification requests (wrong code) return 401 but shouldn't trigger logout
    const body = request.body as any;
    if (body?.phone?.confirmationCode || body?.contactEmail?.confirmationCode) return true;

    return false;
  };

  return initial$.pipe(
    catchError((error: any) => {
      if (isProtected && error instanceof HttpErrorResponse && error.status === 401) {
        if (isBusinessLogic401(error)) {
          return throwError(() => error);
        }
        if (!authService.getAuth()?.accessToken) {
          return throwError(() => error);
        }
        const tokenBefore = authService.getAuth()?.accessToken;
        return from(authService.ensureValidToken(60000)).pipe(
          switchMap(() => {
            if (authService.getAuth()?.accessToken === tokenBefore) {
              return throwError(() => error);
            }
            return next(withAuthHeader(request));
          }),
          catchError((refreshErr) => {
            if (!authService.isNetworkError(refreshErr)) {
              handleRefreshFailure();
            }
            return throwError(() => error);
          })
        );
      }
      return throwError(() => error);
    })
  );
};
